import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import happyHourService from '../../../../services/happyHourService';
import { HappyHourRuleListDTO } from '../../../../types';
import { ArrowLeft, Plus, Pencil, Trash2, Clock, Tag, CheckCircle2, XCircle } from 'lucide-react';
import '../RestaurantPage.css';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const formatDays = (mask: number) => {
  if (mask === 0) return 'Every day';
  return DAYS.filter((_, i) => mask & (1 << i)).join(', ');
};

const HappyHourListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [rules,      setRules]      = useState<HappyHourRuleListDTO[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [deleting,   setDeleting]   = useState<number | null>(null);
  const [error,      setError]      = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>((location.state as any)?.message ?? null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await happyHourService.getAll();
      const d = (res.data as any)?.data ?? [];
      setRules(Array.isArray(d) ? d : []);
    } catch {
      setError('Failed to load happy hour rules.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    setDeleting(id);
    try {
      await happyHourService.delete(id);
      setRules(prev => prev.filter(r => r.id !== id));
      setSuccessMsg('Happy hour rule deleted.');
    } catch {
      setError('Failed to delete rule.');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <Layout>
      <div className="rest-list-page">

        {/* Top bar */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button className="ims-back-btn" onClick={() => navigate('/restaurant/menu-items')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div>
              <div className="ims-page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Clock size={18} style={{ color: 'var(--ims-accent)' }} />
                Happy Hour Rules
              </div>
              <div className="ims-page-subtitle">Time-based discount rules for selected menu items</div>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button className="ims-btn ims-btn-primary" onClick={() => navigate('/restaurant/happy-hours/create')}>
              <Plus size={14} strokeWidth={2} /> New Rule
            </button>
          </div>
        </div>

        {successMsg && (
          <div className="ims-alert ims-alert-success" style={{ marginTop: '1rem' }}>
            {successMsg}
            <button className="btn-close" onClick={() => setSuccessMsg(null)} />
          </div>
        )}
        {error && (
          <div className="ims-alert ims-alert-error" style={{ marginTop: '1rem' }}>
            {error}
            <button className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <div className="ims-card" style={{ marginTop: '1rem' }}>
          <div className="ims-card-body" style={{ padding: 0 }}>
            {loading ? (
              <div style={{ padding: '3rem', textAlign: 'center' }}>
                <div className="spinner-border spinner-border-sm" role="status" />
              </div>
            ) : rules.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--ims-text-sub)', fontSize: 13 }}>
                <Clock size={40} strokeWidth={1.2} style={{ marginBottom: 12, opacity: 0.4 }} />
                <div style={{ fontWeight: 500, marginBottom: 4 }}>No happy hour rules yet</div>
                <div>Create a rule to offer time-based discounts on your menu.</div>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    {['Name', 'Days', 'Time Window', 'Discount', 'Scope', 'Status', ''].map(h => (
                      <th key={h} style={{
                        padding: '10px 14px', background: 'var(--ims-bg-th, #f9fafb)',
                        fontWeight: 600, fontSize: 11, color: 'var(--ims-text-sub)',
                        textTransform: 'uppercase', letterSpacing: '0.04em',
                        textAlign: 'left', borderBottom: '1px solid var(--ims-border, #e5e7eb)',
                        whiteSpace: 'nowrap',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rules.map(rule => (
                    <tr key={rule.id} style={{ borderBottom: '1px solid var(--ims-border, #e5e7eb)' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 500 }}>
                        {rule.name}
                        {rule.description && (
                          <div style={{ fontSize: 11, color: 'var(--ims-text-sub)', marginTop: 2 }}>{rule.description}</div>
                        )}
                      </td>
                      <td style={{ padding: '10px 14px', color: 'var(--ims-text-sub)' }}>
                        {formatDays(rule.activeDays)}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Clock size={12} />
                          {rule.timeFrom} – {rule.timeTo}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          background: '#fef3c7', color: '#92400e',
                          padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600,
                        }}>
                          <Tag size={10} />
                          {rule.discountType === 'Percentage'
                            ? `${rule.discountValue}% off`
                            : `$${Number(rule.discountValue).toFixed(2)} off`}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px', color: 'var(--ims-text-sub)', fontSize: 12 }}>
                        {rule.appliesTo === 'AllItems'
                          ? 'All Items'
                          : rule.appliesTo === 'SelectedCategories'
                            ? 'By Category'
                            : 'Individual Items'}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        {rule.isActive ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#16a34a', fontSize: 12, fontWeight: 600 }}>
                            <CheckCircle2 size={13} /> Active
                          </span>
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#6b7280', fontSize: 12 }}>
                            <XCircle size={13} /> Inactive
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                        <button
                          className="ims-btn ims-btn-secondary"
                          style={{ padding: '4px 10px', fontSize: 12, marginRight: 6 }}
                          onClick={() => navigate(`/restaurant/happy-hours/${rule.id}/edit`)}
                        >
                          <Pencil size={12} /> Edit
                        </button>
                        <button
                          className="ims-btn ims-btn-secondary"
                          style={{ padding: '4px 10px', fontSize: 12, color: '#dc2626', borderColor: '#fca5a5' }}
                          onClick={() => handleDelete(rule.id, rule.name)}
                          disabled={deleting === rule.id}
                        >
                          {deleting === rule.id
                            ? <span className="spinner-border spinner-border-sm" role="status" />
                            : <Trash2 size={12} />}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

      </div>
    </Layout>
  );
};

export default HappyHourListPage;
